import React, { useEffect, useRef } from 'react'
import { animate, useReducedMotion } from 'framer-motion'

/**
 * Animate a number from 0 → target once on mount / when target changes.
 * Writes straight to the DOM node (no per-frame React re-render).
 * Respects prefers-reduced-motion (jumps to the final value).
 *
 * @param {number} target      the value to count up to
 * @param {object} opts
 * @param {number} opts.duration  seconds (default 0.6)
 * @param {(v:number)=>string} opts.format  formats each frame (default rounded, localized)
 * @returns {React.RefObject} ref to attach to the text node
 */
export function useCountUp(target, { duration = 0.6, format = (v) => Math.round(v).toLocaleString() } = {}) {
  const ref = useRef(null)
  const reduce = useReducedMotion()

  useEffect(() => {
    const node = ref.current
    if (!node) return
    const to = Number(target)
    if (!Number.isFinite(to)) { node.textContent = String(target ?? ''); return }
    if (reduce) { node.textContent = format(to); return }

    const controls = animate(0, to, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => { node.textContent = format(v) },
    })
    return () => controls.stop()
  }, [target, reduce, duration, format])

  return ref
}

/**
 * Drop-in numeric text that counts up. Falls back to rendering `value`
 * verbatim when it isn't a finite number (e.g. a loading placeholder).
 */
export function CountUp({ value, format = (v) => Math.round(v).toLocaleString(), duration, className, style }) {
  const ref = useCountUp(value, { duration, format })
  const initial = Number.isFinite(Number(value)) ? format(0) : (value ?? '')
  return React.createElement('span', { ref, className, style }, initial)
}
